<template>
    <b-dropdown no-caret variant="link" toggle-class="filter-by-tag-toggle" menu-class="filter-by-tag-panel" right>
        <template #button-content>
            <font-awesome-icon icon="far fa-filter" />
            <span v-if="localSelectedIds.length" class="filter-count-badge">{{ localSelectedIds.length }}</span>
        </template>

        <div class="filter-by-tag-title">Filter By Tag</div>

        <div v-if="!tags.length" class="filter-by-tag-empty text-muted small">
            No tags created. Add tags to organize your templates
        </div>

        <b-form-checkbox
            v-for="tag in tags"
            :key="tag.id"
            v-model="localSelectedIds"
            :value="tag.id"
            class="filter-by-tag-item"
        >
            {{ tag.name }}
        </b-form-checkbox>

        <div v-if="localSelectedIds.length" class="remove-filters">
            <a href="#" class="remove-filters-link" @click.prevent="clearFilters">
                Remove Filters <font-awesome-icon icon="fas fa-times" />
            </a>
        </div>
    </b-dropdown>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import type { Tag } from '@/api/typings/tags';

const props = defineProps<{
    value: number[];
    tags: Tag[];
}>();

const emit = defineEmits<{
    (event: 'change', value: number[]): void;
}>();

const localSelectedIds = ref<number[]>([...(props.value ?? [])]);

const sameIds = (a: number[], b: number[]) => {
    if (a.length !== b.length) {
        return false;
    }
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((id, index) => id === sortedB[index]);
};

// Only re-sync from the prop when it actually differs from local state — otherwise the
// prop echo (parent re-emits the same ids back down) and this watcher's own emit would
// keep re-triggering each other in an infinite loop.
watch(
    () => props.value,
    (value) => {
        const incoming = value ?? [];
        if (!sameIds(incoming, localSelectedIds.value)) {
            localSelectedIds.value = [...incoming];
        }
    }
);

watch(localSelectedIds, (value) => {
    emit('change', [...value]);
});

const clearFilters = () => {
    localSelectedIds.value = [];
};
</script>

<style lang="scss" scoped>
@import 'styles/utilities/functions';

::v-deep(.filter-by-tag-toggle) {
    svg {
        color: get-color('purple-dark');
    }
}

::v-deep(.filter-by-tag-panel) {
    width: 220px;
    padding: 0;
    overflow: hidden;
    border: 1px solid get-color('gray-light');
    border-radius: 0.25rem;
}

.filter-by-tag-title,
.filter-by-tag-empty,
.filter-by-tag-item {
    padding: 0.375rem 0.75rem;
    margin: 0;
    background-color: get-color('white');
    border-bottom: 1px solid get-color('gray-light');
}

.filter-by-tag-title {
    color: get-color('purple');
    font-weight: bold;
    font-size: 0.875rem;
}

.filter-by-tag-empty {
    font-size: 0.875rem;
}

.filter-by-tag-item {
    padding-left: 2.35rem !important;
    font-size: 0.875rem;
    color: get-color('gray-600');

    &.custom-control {
        display: flex;
        align-items: center;
    }

    ::v-deep .custom-control-label {
        display: flex;
        align-items: center;
        line-height: normal;
        white-space: nowrap;

        &::before,
        &::after {
            top: 50%;
            transform: translateY(-50%);
        }
    }
}

.filter-count-badge {
    background-color: get-color('purple');
    color: #fff;
    border-radius: 50%;
    font-size: 0.65rem;
    padding: 0.1rem 0.35rem;
    margin-left: 0.25rem;
    vertical-align: top;
}

.remove-filters {
    text-align: right !important;
    margin-top: 0.3rem !important;
    margin-right: 0.5rem !important;
}

.remove-filters-link {
    display: inline-block;
    margin-top: 0.25rem;
    font-size: 0.8rem;
}
</style>
